import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { FileText, Download, Copy } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

function fmtCHF(n) { return `CHF ${n.toLocaleString("fr-CH")}`; }

export default function Contracts() {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/contracts/templates");
        setTemplates(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const view = async (id) => {
    try {
      const { data } = await api.get(`/contracts/templates/${id}`);
      setActive(data);
      setOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadText = (t) => {
    const blob = new Blob([t.body], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${t.id}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-8 fade-in-up" data-testid="contracts-page">
      <div className="mb-8">
        <div className="eyebrow">Smart-contract templates</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight mt-1">
          Legal Instruments
        </h1>
        <p className="text-slate-600 mt-2 text-sm md:text-base">
          Swiss-arbitration-ready license, assignment, NDA, and escrow templates. Placeholders are filled at execution time.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {templates.map((t) => (
          <div key={t.id} className="bengal-card p-6" data-testid={`contract-${t.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <FileText size={20} className="text-[#B33A3A] mb-3" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl font-medium tracking-tight">{t.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t.description}</p>
              </div>
              <div className="text-right shrink-0">
                <div className="eyebrow text-[10px]">One-time</div>
                <div className="font-mono text-xl text-[#B33A3A] font-semibold">{fmtCHF(t.fee_chf)}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-5 text-xs text-slate-500">
              <span><strong className="text-slate-700">Term:</strong> {t.duration}</span>
              <span><strong className="text-slate-700">Jurisdictions:</strong> {t.jurisdictions.join(", ")}</span>
            </div>
            <div className="flex items-center gap-2 mt-5 pt-5 border-t border-slate-100">
              <button onClick={() => view(t.id)} className="text-sm bengal-btn-primary" data-testid={`view-${t.id}`}>
                Preview template
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="contract-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{active?.title}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto flex-1">
            <pre className="text-xs text-slate-700 bg-slate-50 border border-slate-200 p-4 whitespace-pre-wrap font-mono leading-relaxed">
              {active?.body}
            </pre>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <div className="text-xs text-slate-500">Fee: <span className="font-mono text-[#B33A3A] font-semibold">{active && fmtCHF(active.fee_chf)}</span></div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { navigator.clipboard.writeText(active?.body || ""); toast.success("Copied"); }}
                className="text-xs border border-slate-300 px-3 py-1.5 rounded-sm hover:border-[#B33A3A] hover:text-[#B33A3A] flex items-center gap-1.5"
                data-testid="copy-contract"
              >
                <Copy size={12} /> Copy
              </button>
              <button
                onClick={() => active && downloadText(active)}
                className="bengal-btn-primary text-xs flex items-center gap-1.5"
                data-testid="download-contract"
              >
                <Download size={12} /> Download .txt
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
